import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function LeadForm({ type = "demo", submitLabel = "Request Demo" }) {
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", fleet_size: "", message: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please provide your name and email.");
      return;
    }
    if (!agreed) {
      toast.error("Please accept the Terms of Service and Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { ...form, type });
      setDone(true);
      toast.success(data.message || "Thanks — we'll be in touch.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="lead-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h3 className="mt-4 font-display text-xl font-bold text-slate-900">Request received</h3>
        <p className="mt-2 text-sm text-slate-600">
          A RideStamp specialist will reach out to {form.email} shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="lf-name" className="text-slate-700">Full name *</Label>
          <Input id="lf-name" value={form.name} onChange={set("name")} placeholder="Jane Doe"
            className="mt-1.5" data-testid="lead-name" />
        </div>
        <div>
          <Label htmlFor="lf-email" className="text-slate-700">Work email *</Label>
          <Input id="lf-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com"
            className="mt-1.5" data-testid="lead-email" />
        </div>
        <div>
          <Label htmlFor="lf-company" className="text-slate-700">Company</Label>
          <Input id="lf-company" value={form.company} onChange={set("company")} placeholder="Metro Mobility"
            className="mt-1.5" data-testid="lead-company" />
        </div>
        <div>
          <Label htmlFor="lf-phone" className="text-slate-700">Phone</Label>
          <Input id="lf-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 000-0000"
            className="mt-1.5" data-testid="lead-phone" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="lf-fleet" className="text-slate-700">Fleet size</Label>
          <Input id="lf-fleet" value={form.fleet_size} onChange={set("fleet_size")} placeholder="e.g. 25 vehicles"
            className="mt-1.5" data-testid="lead-fleet" />
        </div>
      </div>
      <div>
        <Label htmlFor="lf-message" className="text-slate-700">
          {type === "demo" ? "What would you like to see?" : "Message"}
        </Label>
        <Textarea id="lf-message" value={form.message} onChange={set("message")} rows={4}
          placeholder="Tell us about your operation..." className="mt-1.5" data-testid="lead-message" />
      </div>
      <div className="flex items-start gap-3" data-testid="lead-terms">
        <Checkbox id="lf-agree" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)}
          className="mt-0.5" data-testid="lead-terms-checkbox" />
        <Label htmlFor="lf-agree" className="text-sm font-normal leading-relaxed text-slate-600">
          I agree to RideStamp's{" "}
          <Link to="/terms" target="_blank" className="font-semibold text-blue-700 hover:underline" data-testid="lead-terms-link">Terms of Service</Link>{" "}
          and{" "}
          <Link to="/privacy" target="_blank" className="font-semibold text-blue-700 hover:underline" data-testid="lead-privacy-link">Privacy Policy</Link>.
        </Label>
      </div>
      <Button type="submit" disabled={loading || !agreed} data-testid="lead-submit"
        className="w-full gap-2 rounded-full bg-blue-800 py-6 text-base font-semibold text-white hover:bg-blue-900 disabled:opacity-60">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {submitLabel}
      </Button>
      <p className="text-center text-xs text-slate-500">
        We'll never share your details. No spam — just a real conversation about your operation.
      </p>
    </form>
  );
}
